/**
 * Orden de gastos alineado con GET /expenses?sort=date_desc en la API:
 * occurredOn DESC, createdAt DESC, id DESC.
 */
export function compareExpensesByRecencyDesc(
  a: { occurredOn: string; createdAt: string; id: string },
  b: { occurredOn: string; createdAt: string; id: string },
): number {
  let c = b.occurredOn.localeCompare(a.occurredOn);
  if (c !== 0) return c;
  c = b.createdAt.localeCompare(a.createdAt);
  if (c !== 0) return c;
  return b.id.localeCompare(a.id);
}

/** occurredOn ASC, createdAt ASC, id ASC (próximas fechas primero). */
export function compareExpensesByDateAsc(
  a: { occurredOn: string; createdAt: string; id: string },
  b: { occurredOn: string; createdAt: string; id: string },
): number {
  let c = a.occurredOn.localeCompare(b.occurredOn);
  if (c !== 0) return c;
  c = a.createdAt.localeCompare(b.createdAt);
  if (c !== 0) return c;
  return a.id.localeCompare(b.id);
}

export function sortExpensesByRecencyDesc<T extends { occurredOn: string; createdAt: string; id: string }>(
  items: T[],
): T[] {
  return [...items].sort(compareExpensesByRecencyDesc);
}

type JournalSortItem = {
  occurredOn: string;
  createdAt: string;
  id: string;
  paidAt?: string | Date | null;
};

function paidAtComparable(paidAt: JournalSortItem["paidAt"]): string | null {
  if (paidAt == null) return null;
  if (paidAt instanceof Date) return paidAt.toISOString();
  return typeof paidAt === "string" ? paidAt : null;
}

function isPaid(item: JournalSortItem): boolean {
  return paidAtComparable(item.paidAt) != null;
}

/**
 * Diario — solo para el **mes calendario actual** (`monthKey === currentYearMonth`):
 * 1) Impagados con fecha ≥ hoy (próximos por `occurredOn` ascendente)
 * 2) Impagados con fecha anterior a hoy (pendientes / vencidos, fecha ascendente)
 * 3) Pagados al final (`date_desc` como el listado API)
 *
 * Otros meses: mismo criterio que la API (`date_desc`).
 *
 * Nota: `todayIso` debe ser la fecha local del usuario cuando el llamador corre en el cliente;
 * en servidor sin zona del usuario, el runtime usa el calendario local del host.
 */
export function sortJournalExpensesForMonth<T extends JournalSortItem>(
  items: T[],
  monthKey: string,
  ctx: { currentYearMonth: string; todayIso: string },
): T[] {
  if (monthKey !== ctx.currentYearMonth) {
    return sortExpensesByRecencyDesc(items);
  }
  const { todayIso } = ctx;
  const upcomingUnpaid: T[] = [];
  const overdueUnpaid: T[] = [];
  const paid: T[] = [];

  for (const e of items) {
    if (isPaid(e)) {
      paid.push(e);
    } else if (e.occurredOn >= todayIso) {
      upcomingUnpaid.push(e);
    } else {
      overdueUnpaid.push(e);
    }
  }

  upcomingUnpaid.sort(compareExpensesByDateAsc);
  overdueUnpaid.sort(compareExpensesByDateAsc);
  paid.sort(compareExpensesByRecencyDesc);

  return [...upcomingUnpaid, ...overdueUnpaid, ...paid];
}
